import type {
  BoundingBox,
  MapClient,
  MapCoords,
  MapFeatureData,
} from "../../types";
import type { Map as MapboxMap } from "mapbox-gl";

export class MapboxClient implements MapClient<MapboxMap> {
  constructor(private map: MapboxMap) {}

  getBounds() {
    const bounds = this.map.getBounds();
    if (!bounds) {
      return null;
    }

    return {
      sw: { lat: bounds.getSouth(), lng: bounds.getWest() },
      ne: { lat: bounds.getNorth(), lng: bounds.getEast() },
    };
  }

  setBounds(bounds: { sw: MapCoords; ne: MapCoords }, options?: { animate?: boolean }) {
    this.map.fitBounds([bounds.sw, bounds.ne], { animate: options?.animate });
  }

  goToCoords(coords: MapCoords, zoomLevel?: number) {
    this.map.jumpTo({
      center: [coords.lng, coords.lat],
      zoom: zoomLevel ?? this.map.getZoom(),
    });
  }

  getZoomLevel(): number {
    return this.map.getZoom();
  }

  setZoomLevel(zoomLevel: number): void {
    this.map.setZoom(zoomLevel);
  }

  getCoords(): MapCoords {
    const center = this.map.getCenter();
    return {
      lat: center.lat,
      lng: center.lng,
    };
  }

  queryRenderedFeatures(): MapFeatureData[] {
    return this.map.queryRenderedFeatures().map((feature) => ({
      name: feature.properties?.name,
      class: feature.properties?.class,
    }));
  }

  getBoundingBoxImageUrl({
    token,
    bounds,
    width,
    height,
    is2x,
  }: {
    token: string;
    bounds: BoundingBox;
    width: number;
    height: number;
    is2x?: boolean;
  }): string {
    const bbox = [
      bounds.sw.lng,
      bounds.sw.lat,
      bounds.ne.lng,
      bounds.ne.lat,
    ].join(",");

    const url = `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/${bbox}/${width}x${height}${
      is2x ? "@2x" : ""
    }?access_token=${token}`;

    return url;
  }

  getRawClient(): MapboxMap {
    return this.map;
  }
}
